import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_BASE } from "../config";

const LoginForm = () => {
  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    try {
      const res = await axios.post(`${API_BASE}/api/auth/login`, formData);
      const { token, user } = res.data;

      if (!token || !user) throw new Error("Invalid server response");

      // ✅ Flattened structure (same as working dashboard logic)
      const updatedUser = {
        ...user,
        token,
        subscriptionExpiresAt: user.subscriptionExpiresAt || null,
        purchasedPlans: user.purchasedPlans || [],
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      window.dispatchEvent(new Event("userUpdated"));

      if (user.role === "admin") {
        navigate("/admin/overview");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message?.toLowerCase?.() ||
        err?.response?.data?.error?.toLowerCase?.() ||
        "";

      const userInput = formData.identifier.trim();
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInput);

      let msg = "Login failed. Please check your credentials.";

      // 🧩 Smart client-side inference:
      if (status === 401 || backendMessage.includes("invalid credentials")) {
        if (isEmail)
          msg = "Incorrect password for this email. Please try again.";
        else msg = "Incorrect password or username. Try again.";
      } else if (status === 404 || backendMessage.includes("not found")) {
        if (isEmail)
          msg = "No account found with this email address.";
        else msg = "No account found with this username.";
      } else if (backendMessage.includes("password")) {
        msg = "Incorrect password. Please try again.";
      } else if (backendMessage.includes("email")) {
        msg = "Email not registered. Please sign up first.";
      } else if (backendMessage.includes("username")) {
        msg = "Username not recognized. Please check your entry.";
      } else if (!err.response) {
        msg =
          "Network or server issue. Please check your internet connection or try again later.";
      }

      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-md w-full mx-auto bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-2xl p-8 shadow-2xl animate-fade-up transition-colors duration-700"
      aria-label="Login Form"
    >
      <h2 className="text-3xl font-bold text-blue-900 dark:text-yellow-400 mb-6 text-center">
        Welcome Back
      </h2>

      {/* ⚠️ Animated Error Message */}
      <AnimatePresence>
        {errorMessage && (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mb-5 flex items-center gap-3 p-3 rounded-lg bg-red-100 border border-red-300 text-red-700 dark:bg-red-900/40 dark:text-red-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 flex-shrink-0 text-red-500 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M12 5a7 7 0 100 14 7 7 0 000-14z"
              />
            </svg>
            <p className="text-sm font-medium">{errorMessage}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Username or Email */}
      <input
        id="identifier"
        name="identifier"
        type="text"
        placeholder="Username or Email"
        className="w-full px-5 py-3 mb-6 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-yellow-400 transition-all duration-200"
        value={formData.identifier}
        onChange={handleChange}
        required
        autoComplete="username"
      />

      {/* Password */}
      <div className="relative mb-4">
        <input
          id="password"
          name="password"
          type={showPassword ? "text" : "password"}
          placeholder="Password"
          className="w-full px-5 py-3 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-yellow-400 transition-all duration-200"
          value={formData.password}
          onChange={handleChange}
          required
          autoComplete="current-password"
        />

        {/* Password Toggle */}
        <button
          type="button"
          aria-label={showPassword ? "Hide password" : "Show password"}
          onClick={() => setShowPassword((prev) => !prev)}
          className="absolute inset-y-0 right-3 flex items-center text-sm font-medium text-blue-600 dark:text-yellow-300 hover:text-blue-800 dark:hover:text-yellow-400 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-yellow-400 rounded transition-colors"
        >
          {showPassword ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M3.98 8.223a10.477 10.477 0 00-.478 3.777c.563 3.953 4.015 7 8.498 7 2.482 0 4.732-1.013 6.363-2.64M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke="currentColor" strokeWidth={2} d="M3 3l14 14" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5s7.268 2.943 9.542 7c-2.274 4.057-6.065 7-9.542 7s-7.268-2.943-9.542-7z"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Forgot Password */}
      <div className="flex justify-end mb-6">
        <button
          type="button"
          onClick={() => navigate("/forgot-password")}
          className="text-sm text-blue-700 dark:text-yellow-400 hover:text-blue-900 dark:hover:text-yellow-300 underline transition-colors"
        >
          Forgot Password?
        </button>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className={`w-full bg-blue-900 hover:bg-blue-800 dark:bg-yellow-400 dark:hover:bg-yellow-300 text-white dark:text-gray-900 font-semibold py-3 rounded-lg shadow-md focus:ring-4 focus:ring-blue-400 dark:focus:ring-yellow-400 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
      >
        {loading ? "Logging in..." : "Login"}
      </button>

      {/* Signup */}
      <button
        type="button"
        onClick={() => navigate("/signup")}
        className="w-full mt-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-blue-900 dark:text-yellow-300 font-medium py-3 rounded-lg shadow-inner focus:ring-4 focus:ring-blue-200 dark:focus:ring-yellow-300 transition-all duration-200"
      >
        Don’t have an account? Sign Up
      </button>
    </form>
  );
};

export default LoginForm;
